import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api";
import WhatsAppGlyph from "./WhatsAppGlyph";

// Floating WhatsApp contact button — fixed to the bottom-right corner of
// the viewport (not the page), so it stays put through any amount of
// scrolling instead of drifting away with whatever section happens to be
// underneath it. Mounted on Landing and Dashboard. Reads its own on/off
// switch and number from the backend (Admin -> Weekly Release Manager ->
// Support contact) rather than being hardcoded, so support can be turned
// off site-wide (or the number changed) without a code deploy.
export default function WhatsAppButton() {
    const [settings, setSettings] = useState(null); // {whatsapp_enabled, whatsapp_number} | null while loading
    const [bubbleOpen, setBubbleOpen] = useState(false);
    const [bubbleDismissed, setBubbleDismissed] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/settings/support");
                setSettings(data);
            } catch {
                setSettings({ whatsapp_enabled: false, whatsapp_number: "" });
            }
        })();
    }, []);

    // Pops the "have a question?" bubble open a beat after landing on the
    // page, rather than instantly — reads as a considerate nudge, not a
    // pushy popup that's already there before anyone's even looked around.
    // Skipped entirely on narrow viewports: on a phone the hero's own text
    // wraps onto enough extra lines that its CTA buttons land in the exact
    // same bottom-corner strip this bubble occupies — confirmed live, the
    // bubble was sitting directly on top of "Play the first rhyme",
    // covering part of the button. The plain round button below still
    // renders and still opens WhatsApp on tap either way.
    useEffect(() => {
        if (!settings?.whatsapp_enabled) return;
        if (window.matchMedia("(max-width: 639px)").matches) return;
        const id = setTimeout(() => setBubbleOpen(true), 1600);
        return () => clearTimeout(id);
    }, [settings?.whatsapp_enabled]);

    if (!settings?.whatsapp_enabled || !settings.whatsapp_number) return null;

    const digits = settings.whatsapp_number.replace(/\D/g, "");
    // Pre-fills the chat with the Wati trigger keyword so it lands the
    // guardian straight into the "poems support" bot flow instead of a
    // blank chat. WhatsApp's click-to-chat API can prefill the message box
    // but can't submit it for the user — they still tap Send once the app
    // opens — this is a WhatsApp platform restriction, not something we
    // control from here.
    const WHATSAPP_TRIGGER_TEXT = "poems support";
    const waLink = `https://wa.me/${digits}?text=${encodeURIComponent(WHATSAPP_TRIGGER_TEXT)}`;
    const openChat = () => window.open(waLink, "_blank", "noopener,noreferrer");

    return (
        <div className="fixed bottom-5 right-5 z-50 flex flex-col items-end gap-3" data-testid="whatsapp-widget">
            <AnimatePresence>
                {bubbleOpen && !bubbleDismissed && (
                    <motion.div
                        initial={{ opacity: 0, y: 12, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 12, scale: 0.9 }}
                        transition={{ type: "spring", stiffness: 300, damping: 22 }}
                        className="relative max-w-[220px] rounded-2xl bg-white border border-honeySoft shadow-xl shadow-black/10 p-4 cursor-pointer"
                        onClick={openChat}
                        data-testid="whatsapp-bubble"
                    >
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setBubbleDismissed(true); }}
                            className="absolute -top-2 -right-2 rounded-full bg-white border border-honeySoft p-1 text-mocha hover:text-terracotta shadow-sm"
                            aria-label="Dismiss"
                            data-testid="whatsapp-bubble-dismiss"
                        >
                            <X className="w-3.5 h-3.5" strokeWidth={2.8} />
                        </button>
                        <p className="font-display font-bold text-sm text-deepGreen">Have any question?</p>
                        <p className="text-mocha text-xs mt-1">Contact us on WhatsApp</p>
                    </motion.div>
                )}
            </AnimatePresence>

            <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 260, damping: 18, delay: 0.2 }}
            >
                {/* Gentle continuous bob + pulse (little-big-small) so the
                    button stays noticeable without being an obnoxious
                    non-stop spin — separate wrapper so it doesn't fight the
                    entrance spring or the hover/tap scale below. */}
                <motion.div
                    animate={{ y: [0, -8, 0], scale: [1, 1.08, 1] }}
                    transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut", delay: 1.2 }}
                >
                    <motion.button
                        type="button"
                        onClick={openChat}
                        whileHover={{ scale: 1.06 }}
                        whileTap={{ scale: 0.94 }}
                        className="rounded-full bg-[#25D366] p-4 shadow-lg shadow-black/20"
                        aria-label="Contact us on WhatsApp"
                        data-testid="whatsapp-button"
                    >
                        <WhatsAppGlyph className="w-7 h-7 text-white" />
                    </motion.button>
                </motion.div>
            </motion.div>
        </div>
    );
}

// WhatsAppGlyph itself now lives in its own file (WhatsAppGlyph.jsx) —
// WhatsAppCommunityCard.jsx needs the exact same icon and used to just get
// a generic lucide MessageCircle instead, reading as a different, unbranded
// "chat" icon next to this button's real one.
